import { map, latLng, tileLayer, MapOptions, latLngBounds, Map, marker, LatLngBoundsExpression, icon, LatLng, popup, Marker } from "leaflet";
import { TApiLab } from "../../../lib/types/labs";
import { MutableRefObject, useEffect, useState } from "react";
import { useAuthContext } from "../../../contexts/auth-context";

let myMap: {
    map?: Map;
    markers: Marker[];
} = {
    map: undefined,
    markers: [],
};

const INITIAL_ZOOM = 12;
const MAX_ZOOM = 18;
const MIN_ZOOM = 3;

const markerIcon = icon({
    iconUrl: "/images/global/map-marker-icon.png",
    iconSize: [38, 55],
    iconAnchor: [19, 55],
    popupAnchor: [0, -50],
});

// Center of USA
const DEFAULT_LAT = 39.5;
const DEFAULT_LNG = -98.35;

export const parseLatLng = (value?: string | null): LatLng => {
    if (!value) {
        return latLng(DEFAULT_LAT, DEFAULT_LNG);
    }

    const parts = value.split(",").map((n) => parseFloat(n.trim()));
    const lat = parts[0];
    const lng = parts[1];

    const isValid = !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

    return isValid ? latLng(lat, lng) : latLng(DEFAULT_LAT, DEFAULT_LNG);
};

export const useMapLabLogic = (mapRef: React.MutableRefObject<any>) => {
    const mapInitialSetup: MapOptions = {
        zoom: INITIAL_ZOOM,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
    };
    const { isLoggedIn } = useAuthContext();

    const [selectedLab, setSelectedLab] = useState<TApiLab | null>(null);

    const setMarkers = (labs: TApiLab[], map: Map, showLabName: boolean) => {
        if (myMap.markers.length > 0) {
            myMap.markers.forEach((m) => m.removeFrom(map));
        }

        if (labs.length === 0) return;

        labs.forEach((l) => {
            const latLng = getLabLatLng(l);
            const labMarker = marker(latLng, {
                icon: markerIcon,
            });

            const thePopup = popup({
                closeButton: false,
            })
                .setLatLng(latLng)
                .setContent(`<strong class="${showLabName ? "" : "blurredText"}" >${l.name}</strong><br/>${l.other.positions.length} open positions`);

            labMarker.bindPopup(thePopup);

            labMarker.addEventListener("mouseover", (_e) => {
                labMarker.openPopup();

                const labElements = document.getElementsByClassName(`scrollToLab-${l.id}`);
                const labListContainer = document.querySelector(".lablist-map__scroll");

                if (labElements && labElements[0] && labListContainer) {
                    Array.of(...labElements).forEach((labElement) => {
                        const labHTMLElement = labElement as HTMLElement;

                        const containerTop = labListContainer.getBoundingClientRect().top;
                        const elementTop = labHTMLElement.getBoundingClientRect().top;

                        const scrollOffset = elementTop - containerTop - 50;

                        labListContainer.scrollBy({
                            top: scrollOffset,
                            behavior: "smooth",
                        });

                        labHTMLElement.getElementsByClassName("card-maps")[0].classList.add("card-selected");
                    });
                }
            });

            labMarker.addEventListener("mouseout", () => {
                thePopup.close();
                const labElements = document.getElementsByClassName(`scrollToLab-${l.id}`);
                if (labElements && labElements[0]) {
                    Array.of(...labElements).forEach((e) => {
                        e.getElementsByClassName("card-maps")[0].classList.remove("card-selected");
                    });
                }
            });

            labMarker.addEventListener("click", () => {
                setSelectedLab(l);
            });
            //labMarker.bindPopup(`<b><img style="height: 70px" src="${getImagePath(l.images?.logo, l.id)}"/> <br/> ${l.name}</b>`);
            labMarker.addTo(map);
            myMap.markers.push(labMarker);
        });
    };

    const getGeoBounds = (labs: TApiLab[]): LatLngBoundsExpression => {
        if (labs.length === 0) return latLngBounds(latLng(DEFAULT_LAT, DEFAULT_LNG), latLng(DEFAULT_LAT, DEFAULT_LNG));

        const coords = labs.map((l) => parseLatLng(l.zip_code_location));
        const lats = coords.map((c) => c.lat);
        const longs = coords.map((c) => c.lng);

        return latLngBounds(latLng(Math.min(...lats), Math.min(...longs)), latLng(Math.max(...lats), Math.max(...longs)));
    };

    const enableMap = (labs: TApiLab[], mapReference?: MutableRefObject<any>) => {
        if (!(mapReference || mapRef).current || !myMap) {
            setTimeout(() => {
                enableMap(labs, mapReference);
            }, 1000);
            return;
        }

        if (!myMap.map || mapReference) {
            myMap.map = map(mapReference?.current || mapRef.current, mapInitialSetup);
            tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            }).addTo(myMap.map);
        }

        if (labs.length > 0) {
            myMap.map.fitBounds(getGeoBounds(labs));
        } else {
            myMap.map.setView([DEFAULT_LAT, DEFAULT_LNG], MIN_ZOOM);
        }

        setMarkers(labs, myMap.map, isLoggedIn);
    };

    const getLabLatLng = (lab: TApiLab): LatLng => {
        return parseLatLng(lab.zip_code_location);
    };

    const centerLabOnMap = (lab: TApiLab) => {
        myMap.map?.setView(getLabLatLng(lab), 15);
    };

    useEffect(() => {
        return () => {
            myMap.map?.remove();
            myMap.map = undefined;
        };
    }, []);

    return {
        centerLabOnMap,
        enableMap,
        selectedLab,
        testable_getGeoBounds: getGeoBounds,
    };
};
